import { Grid } from '@mui/material';
import { CustomFields, ReportTask } from '@nx-smartmonkey/shared/domain';
import { RoutalButton as Button, CrossIcon, HSpacer, RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';

import NorthStarIcon from '../../../../../../../../assets/img/shared/icon-northStar.svg';
import ScanIcon from '../../../../../../../../assets/svg/BarcodeIcon';
import { PlusIcon } from '../../../../../../../../assets/svg/PlusIcon';
import { Modal } from '../../../../../../../../components/Modal';
import { STOP_TASK_STATUS, StopTaskStatus } from '../../../../../../../../constants/Stops';
import { DriverProjectFields } from '../../../../../../../../domain/driversProject/DriversProject';
import { Stop } from '../../../../../../../../domain/stops/Stop';
import { StopTask } from '../../../../../../../../domain/stops/StopTask';
import { Text } from '../../../../../../../../react-components/Text';
import { useRetrieveDriverCredentials } from '../../../../../../hooks/useRetrieveDriverCredentials';
import { useRetrieveVehicle } from '../../../../../../hooks/useRetrieveVehicle';
import { StopReportScanner } from '../report-actions/scanner/StopReportScanner';
import { TaskCreate } from './TaskCreate';
import { TaskCustomFields } from './TaskCustomFields';
import { TaskElement } from './TaskElement';
import { TaskFooterButton } from './TaskFooterButton';

interface StopCompleteTasksProps {
  show: boolean;
  stop: Stop;
  onClose: (stopId: string) => void;
  onComplete: (reportTasks: ReportTask[], temporalReportTasks: ReportTask[], temporalTasks: StopTask[]) => void;
}

export const StopCompleteTasks = ({ stop, onClose, onComplete, show }: StopCompleteTasksProps) => {
  const { data: vehicleData } = useRetrieveVehicle();
  const { data: credentials } = useRetrieveDriverCredentials();

  const [scanning, setScanning] = useState(false);
  const [reportTasks, setReportTasks] = useState<Record<string, ReportTask>>({});
  const [temporalReportTasks, setTemporalReportTasks] = useState<Record<string, ReportTask>>({});
  const [temporalTasks, setTemporalTasks] = useState<StopTask[]>([]);
  const [allTasks, setAllTasks] = useState<StopTask[]>(stop ? stop.tasks : []);
  const [expandedTasks, setExpandedTasks] = useState<Record<string, boolean>>({});

  const [taskCustomFields, setTaskCustomFields] = useState<DriverProjectFields>([]);
  const [selectedTask, setSelectedTask] = useState<StopTask | null>(null);

  const [isConfirming, setIsConfirming] = useState(false);
  const [isForcedContinue, setIsForcedContinue] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [isCreatingTasks, setIsCreatingTasks] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState<string | undefined>(undefined);
  const FOOTER_SIZE = 75;

  /**
   * When the component is created, initialize the tasks and expanded tasks
   */
  useEffect(() => {
    // In case the tasks are enabled and the stop has tasks
    if (vehicleData?.project.tasks_enabled && allTasks) {
      // Initializing the ReportTask react state.
      const mappedReportTasks = stop.tasks.reduce(
        (prev, curr) => ({
          ...prev,
          [curr.id]: ReportTask.create({
            task_id: curr.id,
            status: STOP_TASK_STATUS.pending,
          }),
        }),
        {}
      ) as { [id: string]: ReportTask };
      setReportTasks(mappedReportTasks);

      const mappedTemporalReportTasks = temporalTasks.reduce(
        (prev, curr) => ({
          ...prev,
          [curr.id]: ReportTask.create({
            task_id: curr.id,
            status: STOP_TASK_STATUS.pending,
          }),
        }),
        {}
      ) as { [id: string]: ReportTask };
      setTemporalReportTasks(mappedTemporalReportTasks);

      // Initializing the expanded tasks react state.
      const mappedExpandedTasks = allTasks.reduce(
        (prev, curr) => ({
          ...prev,
          [curr.id]: false,
        }),
        {}
      ) as { [id: string]: boolean };
      setExpandedTasks(mappedExpandedTasks);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleData?.project.tasks_enabled, stop]);

  useEffect(() => {
    if (isForcedContinue) {
      // In case there are pending tasks, we ask for confirmation
      if (
        Object.values(reportTasks).some((task) => task.status === STOP_TASK_STATUS.pending) ||
        Object.values(temporalReportTasks).some((task) => task.status === STOP_TASK_STATUS.pending)
      ) {
        return;
      }
      onComplete(Object.values(reportTasks), Object.values(temporalReportTasks), temporalTasks);
      setIsForcedContinue(false);
      foldAllTasks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportTasks, temporalReportTasks]);

  if (!show || !vehicleData || !credentials) {
    return null;
  }

  /**
   * It counts how many tasks contain a field that is a barcode and has a value
   *
   * @returns The total number of barcodes that the stop has
   */
  const totalBarcodesNumber = (): number => {
    let barcodes = 0;

    // In case there are barcode fields and the stop has tasks
    if (allTasks.length > 0) {
      // We check how many of the tasks has a inner task field that is a barcode and has a value.
      barcodes = allTasks.filter((task) => task.fields.some((field) => field.id === `barcode` && field.value)).length;
    }
    return barcodes;
  };

  /**
   * It counts how many tasks that contain a field that is a barcode and has a value have been completed.
   *
   * @returns The total number of completed tasks that have a barcode field
   */
  const scannedBarcodesNumber = (): number => {
    let scannedBarcodes = 0;
    if (allTasks.length > 0) {
      scannedBarcodes += stop.tasks.reduce((amount, task) => {
        if (
          Object.values(reportTasks).length > 0 &&
          (reportTasks[task.id]?.status === (STOP_TASK_STATUS.pending as StopTaskStatus) ||
            task.fields.some((field) => field.id === `barcode` && !field.value))
        )
          return amount;

        return amount + 1;
      }, 0);
      scannedBarcodes += temporalTasks.reduce((amount, task) => {
        if (
          Object.values(temporalReportTasks).length > 0 &&
          (temporalReportTasks[task.id]?.status === (STOP_TASK_STATUS.pending as StopTaskStatus) ||
            task.fields.some((field) => field.id === `barcode` && !field.value))
        )
          return amount;

        return amount + 1;
      }, 0);
    }

    return scannedBarcodes;
  };

  const allReports = (): Record<string, ReportTask> => {
    return allTasks.reduce((prev, curr) => {
      if (reportTasks[curr.id]) {
        return {
          ...prev,
          [curr.id]: reportTasks[curr.id],
        };
      }
      if (temporalReportTasks[curr.id]) {
        return {
          ...prev,
          [curr.id]: temporalReportTasks[curr.id],
        };
      } else {
        return prev;
      }
    }, {});
  };

  /**
   * It updates the status of a task
   *
   * @param taskId The taskId to update
   * @param status The new status of the task
   * @param custom_fields The custom fields of the task
   */
  const updateTask = (taskId: string, status: StopTaskStatus, custom_fields?: CustomFields) => {
    const { comments, ...cfProps } = custom_fields?.props ?? {};
    let customFields = custom_fields;
    if (comments) customFields = CustomFields.create({ ...cfProps });

    if (stop.tasks.find((task) => task.id === taskId)) {
      setReportTasks({
        ...reportTasks,
        [taskId]: ReportTask.create({
          ...reportTasks[taskId],
          status: status,
          task_id: taskId,
          comments: comments as string,
          custom_fields: customFields,
        }),
      });
      return;
    }

    setTemporalReportTasks({
      ...temporalReportTasks,
      [taskId]: ReportTask.create({
        ...temporalReportTasks[taskId],
        status: status,
        task_id: taskId,
        comments: comments as string,
        custom_fields: customFields,
      }),
    });
  };

  /**
   * It updates the status of a task if its pending
   *
   * @param newStatus The new status of the task
   */
  const updateAllTasks = (newStatus: StopTaskStatus) => {
    const newReportTasks = { ...reportTasks } as Record<string, ReportTask>;

    Object.keys(newReportTasks).forEach((taskId) => {
      if (newReportTasks[taskId].status === STOP_TASK_STATUS.pending) {
        newReportTasks[taskId] = ReportTask.create({
          ...newReportTasks[taskId],
          status: newStatus,
          task_id: taskId,
        });
      }
    });

    setReportTasks(newReportTasks);

    const newTemporalReportTasks = { ...temporalReportTasks } as Record<string, ReportTask>;

    Object.keys(newTemporalReportTasks).forEach((taskId) => {
      if (newTemporalReportTasks[taskId].status === STOP_TASK_STATUS.pending) {
        newTemporalReportTasks[taskId] = ReportTask.create({
          ...newTemporalReportTasks[taskId],
          status: newStatus,
          task_id: taskId,
        });
      }
    });

    setTemporalReportTasks(newTemporalReportTasks);
  };

  /**
   * It manages the task status and the custom fields of the task
   *
   * @param task The task to manage
   * @param status The new status of the task
   * @param taskValues The custom fields of the task in case of a completion from the scanner
   */
  const manageTask = (task: StopTask, status: StopTaskStatus, taskValues?: CustomFields, scanner?: boolean) => {
    if (scanner) {
      if (!allTasks.find((t) => t.id === task.id)) {
        // In case the task is not found in allTasks, it means is a task created by the scanner
        const newTempTasks = [...temporalTasks, task];
        setAllTasks([...(stop ? stop.tasks : []), ...newTempTasks]);
        setTemporalTasks(newTempTasks);

        const mappedTemporalReportTasks = newTempTasks.reduce((prev, curr) => {
          if (temporalReportTasks[curr.id]) {
            return {
              ...prev,
              [curr.id]: temporalReportTasks[curr.id],
            };
          }

          const { comments, ...cfProps } = taskValues?.props ?? {};
          let customFields = taskValues;
          if (comments) customFields = CustomFields.create({ ...cfProps });
          return {
            ...prev,
            [curr.id]: ReportTask.create({
              task_id: curr.id,
              status: status,
              comments: comments as string,
              custom_fields: customFields,
            }),
          };
        }, {});

        setTemporalReportTasks(mappedTemporalReportTasks);
      } else {
        updateTask(task.id, status, taskValues);
      }
      setScanning(credentials.test(`can_update_stop_tasks`) || scannedBarcodesNumber() < totalBarcodesNumber());
      setSelectedTask(null);
      setTaskCustomFields([]);
    } else {
      updateTask(task.id, status);

      const isTemporal = temporalTasks.find((t) => t.id === task.id);

      if (
        (isTemporal
          ? temporalReportTasks[task.id].status !== STOP_TASK_STATUS.completed
          : reportTasks[task.id].status !== STOP_TASK_STATUS.completed) &&
        status === STOP_TASK_STATUS.completed &&
        vehicleData.project.fields.task_completed_fields.length > 0
      ) {
        setTaskCustomFields(vehicleData.project.fields.task_completed_fields);
        setSelectedTask(task);
      }

      if (
        (isTemporal
          ? temporalReportTasks[task.id].status !== STOP_TASK_STATUS.canceled
          : reportTasks[task.id].status !== STOP_TASK_STATUS.canceled) &&
        status === STOP_TASK_STATUS.canceled &&
        vehicleData.project.fields.task_canceled_fields.length > 0
      ) {
        setTaskCustomFields(vehicleData.project.fields.task_canceled_fields);
        setSelectedTask(task);
      }
    }
  };

  /**
   * It checks if there is a next task to set as expanded, if so, it sets it as expanded.
   * In any case it collapses the current task.
   *
   * @param task The task that is going to be collapsed
   */
  const setNextExpanded = (task: StopTask) => {
    const nextTask =
      allTasks[
        allTasks.findIndex(
          (t) =>
            t.id !== task.id &&
            (reportTasks[t.id]
              ? reportTasks[t.id].status === STOP_TASK_STATUS.pending
              : temporalReportTasks[t.id].status === STOP_TASK_STATUS.pending)
        )
      ];

    if (nextTask) {
      setExpandedTasks({
        ...expandedTasks,
        [task.id]: false,
        [nextTask.id]: true,
      });
    } else {
      setExpandedTasks({
        ...expandedTasks,
        [task.id]: false,
      });
    }
  };

  /**
   * Fold all tasks
   */
  const foldAllTasks = () => {
    setExpandedTasks(
      Object.keys(expandedTasks).reduce((prev, curr) => {
        return {
          ...prev,
          [curr]: false,
        };
      }, {})
    );
  };

  /**
   * It checks if there are pending tasks and if so, it asks for confirmation to continue.
   * If there are no pending tasks, it calls the onComplete function.
   *
   * @param force If an object is passed, it forces the completion of the tasks
   */
  const checkAndCommit = (force?: StopTaskStatus) => {
    // In case the user wants to force a cancel of tasks
    if (force) {
      updateAllTasks(force);

      setIsForcedContinue(true);
      setIsConfirming(false);
      return;
    }

    // In case there are pending tasks, we ask for confirmation
    if (
      Object.values(reportTasks).some((task) => task.status === STOP_TASK_STATUS.pending) ||
      Object.values(temporalReportTasks).some((reportTask) => reportTask.status === STOP_TASK_STATUS.pending)
    ) {
      setIsConfirming(true);
      return;
    }

    onComplete(Object.values(reportTasks), Object.values(temporalReportTasks), temporalTasks);
    foldAllTasks();
  };

  if (taskCustomFields.length > 0 && selectedTask) {
    const isTemporal = temporalTasks.find((task) => task.id === selectedTask.id);
    return (
      <TaskCustomFields
        customFields={taskCustomFields}
        task={selectedTask}
        reportTask={isTemporal ? temporalReportTasks[selectedTask.id] : reportTasks[selectedTask.id]}
        status={isTemporal ? temporalReportTasks[selectedTask.id].status : reportTasks[selectedTask.id].status}
        onClose={() => {
          updateTask(selectedTask.id, STOP_TASK_STATUS.pending);
          setSelectedTask(null);
          setTaskCustomFields([]);
        }}
        onConfirm={(taskValues: CustomFields) => {
          updateTask(
            selectedTask.id,
            isTemporal ? temporalReportTasks[selectedTask.id].status : reportTasks[selectedTask.id].status,
            taskValues
          );
          setNextExpanded(selectedTask);
          if (scanning) {
            setScanning(scannedBarcodesNumber() < totalBarcodesNumber());
          }
          setSelectedTask(null);
          setTaskCustomFields([]);
        }}
      />
    );
  }

  if (scanning) {
    return (
      <StopReportScanner
        selectedStop={stop}
        reportingTasks={allReports()}
        selectedStopTasks={allTasks}
        totalCodes={totalBarcodesNumber()}
        scannedCodes={scannedBarcodesNumber()}
        onClose={() => setScanning(false)}
        onUpdateTasks={(task: StopTask, taskStatus: StopTaskStatus, taskValues?: CustomFields) => {
          manageTask(task, taskStatus, taskValues, true);
          if (!credentials.test(`can_update_stop_tasks`) && scannedBarcodesNumber() > totalBarcodesNumber()) {
            setScanning(false);
          }
        }}
      />
    );
  }

  return (
    <>
      {/* Modal confirm continue */}
      <Modal
        open={isConfirming}
        title={{
          intlId: `driver.stop.report.tasks.pending_tasks`,
        }}
        subtitle={{
          intlId:
            vehicleData.project.fields.task_completed_fields.some((field) => field.is_required) &&
            vehicleData.project.fields.task_canceled_fields.some((field) => field.is_required)
              ? `driver.stop.report.tasks.pending_tasks_with_requirements_description`
              : vehicleData.project.fields.task_completed_fields.some((field) => field.is_required)
              ? `driver.stop.report.tasks.pending_tasks_complete_requirements_description`
              : vehicleData.project.fields.task_canceled_fields.some((field) => field.is_required)
              ? `driver.stop.report.tasks.pending_tasks_cancel_requirements_description`
              : `driver.stop.report.tasks.pending_tasks_description`,
        }}
        buttons={[
          ...(!vehicleData.project.fields.task_completed_fields.some((field) => field.is_required)
            ? [
                {
                  intlId: `driver.stop.report.tasks.complete_all_short`,
                  onClick: () => {
                    checkAndCommit(STOP_TASK_STATUS.completed as StopTaskStatus);
                  },
                  isLoading: false,
                  style: {
                    backgroundColor: RoutalPalette.success.medium,
                    border: `none`,
                  },
                  disabled: !!vehicleData.project.fields.task_completed_fields.find((field) => field.is_required),
                },
              ]
            : []),
          ...(!vehicleData.project.fields.task_canceled_fields.some((field) => field.is_required)
            ? [
                {
                  intlId: `driver.stop.report.tasks.cancel_all`,
                  onClick: () => {
                    checkAndCommit(STOP_TASK_STATUS.canceled as StopTaskStatus);
                  },
                  isLoading: false,
                  style: {
                    backgroundColor: RoutalPalette.error.medium,
                    border: `none`,
                  },
                  disabled: !!vehicleData.project.fields.task_canceled_fields.find((field) => field.is_required),
                },
              ]
            : []),
          {
            intlId: `driver.stop.report.tasks.continue`,
            onClick: () => {
              setIsConfirming(false);
            },
            variant: `text`,
            style: {
              border: `none`,
            },
          },
        ]}
        children={
          <>
            <VSpacer medium />
            <VSpacer small />
          </>
        }
      />

      {/* Modal for leaving if content */}
      <Modal
        open={showCloseModal}
        title={{
          intlId: `drivers.stop.report.modal.title`,
        }}
        subtitle={{
          intlId: `drivers.stop.report.modal.subtitle`,
        }}
        buttons={[
          {
            intlId: `drivers.stop.report.modal.leave`,
            onClick: () => {
              setShowCloseModal(false);
              onClose(stop.id);
            },
            variant: `text`,
            style: {
              border: `none`,
            },
          },
          {
            intlId: `cancel`,
            onClick: () => {
              setShowCloseModal(false);
            },
          },
        ]}
        children={<VSpacer medium />}
      />

      {/* Modal for trying to delete a task */}
      <Modal
        open={!!showDeleteModal}
        title={{
          intlId: `drivers.stop.new_task.modal.title`,
        }}
        subtitle={{
          intlId: `drivers.stop.new_task.modal.subtitle`,
        }}
        buttons={[
          {
            intlId: `drivers.stop.new_task.modal.confirm`,
            onClick: () => {
              const newTempTasks = temporalTasks.filter((tempTask) => tempTask.id !== showDeleteModal);
              setTemporalTasks(newTempTasks);
              setAllTasks([...stop.tasks, ...newTempTasks]);

              const newTemporalReportTasks = newTempTasks.reduce((prev, curr) => {
                return {
                  ...prev,
                  [curr.id]: temporalReportTasks[curr.id],
                };
              }, {});
              setTemporalReportTasks(newTemporalReportTasks);
              setShowDeleteModal(undefined);
            },
            style: {
              backgroundColor: RoutalPalette.error.dark,
            },
          },
          {
            intlId: `cancel`,
            onClick: () => {
              setShowDeleteModal(undefined);
            },
            variant: `text`,
          },
        ]}
        children={<VSpacer medium />}
      />

      {/* Modal for creating tasks */}
      {isCreatingTasks && (
        <TaskCreate
          onClose={() => {
            setIsCreatingTasks(false);
          }}
          returnTasks={(newTasks: StopTask[]) => {
            const newTempTasks = [...temporalTasks, ...newTasks];
            setAllTasks([...(stop ? stop.tasks : []), ...newTempTasks]);
            setTemporalTasks(newTempTasks);

            const mappedTemporalReportTasks = newTempTasks.reduce((prev, curr) => {
              if (temporalReportTasks[curr.id]) {
                return {
                  ...prev,
                  [curr.id]: temporalReportTasks[curr.id],
                };
              }
              return {
                ...prev,
                [curr.id]: ReportTask.create({
                  task_id: curr.id,
                  status: STOP_TASK_STATUS.pending,
                }),
              };
            }, {});

            setTemporalReportTasks(mappedTemporalReportTasks);
          }}
          lastId={temporalTasks.length > 0 ? Number(temporalTasks[temporalTasks.length - 1].id) + 1 : 0}
          stop={stop}
          footerSize={FOOTER_SIZE}
        />
      )}

      {/* Main Container */}
      <Grid
        style={{
          zIndex: 1004,
          position: `absolute`,
          height: `100%`,
          width: `100%`,
          background: RoutalPalette.white,
          overflowY: `auto`,
          overflowX: `hidden`,
        }}
      >
        {/* Header */}
        <Grid
          display={`flex`}
          flexDirection={`row`}
          justifyContent={`space-between`}
          borderBottom={`1px solid ${RoutalPalette.neutral10}`}
          alignItems={`center`}
          height={`70px`}
          padding={`0px 10px 0px 16px`}
        >
          <Text style={{ fontSize: `18px` }} intlId="driver.stop.report.tasks_field" />

          <Grid display={`flex`} alignItems={`center`}>
            {(credentials.test(`can_update_stop_tasks`) || scannedBarcodesNumber() < totalBarcodesNumber()) && (
              <>
                <Button
                  style={{
                    backgroundColor: RoutalPalette.primary40,
                    height: `45px`,
                    width: `60px`,
                    minWidth: `60px`,
                    borderRadius: `2px`,
                    padding: `0px`,
                  }}
                  onClick={() => {
                    setScanning(true);
                  }}
                >
                  <ScanIcon style={{ height: `22px`, width: `22px`, fill: RoutalPalette.white }} />
                </Button>
                <HSpacer medium />
              </>
            )}
            <Button
              style={{
                backgroundColor: `transparent`,
                minWidth: `fit-content`,
                color: RoutalPalette.grey1,
                display: `flex`,
                alignItems: `center`,
                justifyContent: `center`,
              }}
              onClick={() => {
                if (
                  temporalTasks.length > 0 ||
                  Object.values({ ...reportTasks, ...temporalReportTasks }).some(
                    (task) => task.status !== STOP_TASK_STATUS.pending
                  )
                ) {
                  setShowCloseModal(true);
                  return;
                }
                onClose(stop.id);
              }}
            >
              <CrossIcon style={{ height: `20px`, width: `20px` }} />
            </Button>
          </Grid>
        </Grid>

        {/* Task elements */}
        <Grid
          container
          direction={`column`}
          wrap={`nowrap`}
          style={{ height: `fit-content`, paddingBottom: `${FOOTER_SIZE}px` }}
        >
          {Object.values(reportTasks).map((reportTask) => (
            <TaskElement
              key={reportTask.task_id}
              task={stop.tasks.find((task) => task.id === reportTask.task_id)!}
              reportTask={reportTask}
              updateTask={(task: StopTask, status: StopTaskStatus) => {
                manageTask(task, status);
                if (status !== STOP_TASK_STATUS.pending) {
                  setNextExpanded(task);
                }
              }}
              isExpanded={Boolean(expandedTasks[reportTask.task_id])}
              isTemporal={false}
              updateExpanded={(expanded: boolean) => {
                setExpandedTasks({
                  ...expandedTasks,
                  [reportTask.task_id]: expanded,
                });
              }}
            />
          ))}
          {Object.values(temporalReportTasks).map((reportTask) => (
            <TaskElement
              key={reportTask.task_id}
              task={temporalTasks.find((task) => task.id === reportTask.task_id)!}
              reportTask={reportTask}
              updateTask={(task: StopTask, status: StopTaskStatus) => {
                manageTask(task, status);
                if (status !== STOP_TASK_STATUS.pending) {
                  setNextExpanded(task);
                }
              }}
              isExpanded={Boolean(expandedTasks[reportTask.task_id])}
              isTemporal={true}
              updateExpanded={(expanded: boolean) => {
                setExpandedTasks({
                  ...expandedTasks,
                  [reportTask.task_id]: expanded,
                });
              }}
              deleteTask={(task: StopTask) => {
                setShowDeleteModal(task.id);
              }}
            />
          ))}
          {credentials.test(`can_update_stop_tasks`) && (
            <Button
              variant={`flat`}
              style={{
                margin: `20px`,
                display: `flex`,
                flexDirection: `row`,
                alignItems: `center`,
                border: `1px solid ${RoutalPalette.secondary60}`,
                height: `48px`,
                padding: 0,
              }}
              onClick={() => {
                setIsCreatingTasks(true);
              }}
            >
              <PlusIcon />
              <HSpacer small />
              <Text
                variant="body-medium"
                style={{ color: RoutalPalette.secondary60 }}
                intlId="driver.stop.report.create_task"
              />
            </Button>
          )}
        </Grid>
      </Grid>

      {/* Footer */}
      <TaskFooterButton
        containerStyle={{ height: `${FOOTER_SIZE}px` }}
        buttonStyle={{ textTransform: `none` }}
        onClick={() => {
          checkAndCommit();
        }}
        buttonIntlId="driver.stop.report.complete_task.next"
        buttonColor={RoutalPalette.primary40}
        image={NorthStarIcon}
      />
    </>
  );
};
